"use client";

import { HoverCardProfile2 } from "@/components/account/profile/profile-hover-card";
import { renderMentions } from "@/components/account/profile/render-mentions";
import { Button } from "@/components/ui/button";
import { deleteComment } from "@/lib/blog/comments";
import {
	cn,
	copyToClipboard,
	getBlogPostCommentsUrl,
	scrollToElement,
	timeAgo,
} from "@/lib/utils";
import type { User } from "@supabase/supabase-js";
import { FlagIcon, LinkIcon, ReplyIcon, TrashIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import { CommentForm } from "./comment-form";
import { CommentPostOptions } from "./comment-options";
import type { CommentTreeNode } from "./comments-box";

interface CommentPostProps {
	comment: CommentTreeNode;
	currentUser: User | null;
	depth?: number;
	onReplyAdded?: (parentCommentId: string, reply: CommentTreeNode) => void;
}

export const CommentPost = ({
	comment,
	currentUser,
	depth = 0,
	onReplyAdded,
}: CommentPostProps) => {
	const router = useRouter();

	const [isEditing, setIsEditing] = useState(false);
	const [isReplying, setIsReplying] = useState(false);

	const handleEdit = () => {
		setIsEditing(true);
	};

	const handleDelete = async () => {
		const { data, error } = await deleteComment(comment.user_id, comment.id);
		if (error) {
			console.error(error);
			toast("Oops.. Cannot delete comment with replies", {
				icon: <TrashIcon className="w-4 h-4" />,
				style: {
					background: "red",
					color: "white",
				},
			});
			return;
		}
		toast("Comment deleted successfully", {
			icon: <TrashIcon className="w-4 h-4" />,
			style: {
				background: "green",
				color: "white",
			},
		});
		router.refresh();
	};

	const handleCopy = () => {
		copyToClipboard(getBlogPostCommentsUrl(comment.post_id, comment.id));
		toast("Link copied to clipboard", {
			icon: <LinkIcon className="w-4 h-4" />,
			style: {
				background: "green",
				color: "white",
			},
		});
	};

	const handleReport = () => {
		// TODO: handle reporting
		toast("Thank you for reporting", {
			icon: <FlagIcon className="w-4 h-4" />,
			style: {
				background: "orange",
				color: "white",
			},
		});
	};

	const handleReplyAdded = (
		parentCommentId: string,
		reply: CommentTreeNode,
	) => {
		onReplyAdded?.(parentCommentId, reply);
		setIsReplying(false);
	};

	// Increase indentation for each level
	const getMarginClass = (depth: number) => {
		if (depth === 1) return "ml-4";
		return depth > 0 ? "ml-8" : "";
	};
	const marginLeftClass = getMarginClass(depth);

	return (
		<div className={cn(marginLeftClass)}>
			<article
				id={`comment-${comment.id}`}
				className={cn(
					"w-full p-4 text-primary transition-all",
					isReplying && "bg-primary-34/5 rounded-lg",
					depth > 0 && "border-l border-primary-34/10",
				)}
			>
				<footer className="flex justify-between items-center mb-2">
					<div className="flex gap-2 items-center">
						{comment.user ? (
							<HoverCardProfile2 profile={comment.user} />
						) : (
							<div className="flex items-center gap-2 text-sm text-primary-34">
								<div className="w-8 h-8 rounded-full bg-primary-10 flex items-center justify-center">
									<span className="text-xs font-medium">D</span>
								</div>
								<span>Deleted User</span>
							</div>
						)}
					</div>

					<CommentPostOptions
						comment={comment}
						currentUser={currentUser}
						onEdit={handleEdit}
						onDelete={handleDelete}
						onCopy={handleCopy}
						onReport={handleReport}
					/>
				</footer>

				{isEditing ? (
					<CommentForm
						postId={comment.post_id}
						currentUserId={currentUser?.id || ""}
						comment={comment.comment}
						editCommentId={comment.id}
						showAvatar={false}
						onSubmit={() => setIsEditing(false)}
					/>
				) : (
					<p className="mt-4 text-primary">{renderMentions(comment.comment)}</p>
				)}

				<div className="flex flex-row justify-between items-center mt-2 space-x-4">
					{comment.updated_at ? (
						<p className="text-xs text-primary-34">
							<span className="sr-only">Updated on </span>
							<time dateTime="2022-02-08" title="February 8th, 2022">
								updated: {timeAgo(new Date(comment.updated_at))}
							</time>
						</p>
					) : (
						<p className="text-xs text-primary-34">
							<span className="sr-only">Posted on </span>
							<time dateTime="2022-02-08" title="February 8th, 2022">
								{comment.created_at && timeAgo(new Date(comment.created_at))}
							</time>
						</p>
					)}
					<Button
						type="button"
						onClick={() => {
							setIsReplying(true);
							scrollToElement(`comment-${comment.id}`);
						}}
						variant="outline"
						className="flex items-center rounded-full text-sm text-primary hover:underline font-medium"
					>
						<ReplyIcon className="mr-1.5 w-3.5 h-3.5" />
						Reply
					</Button>
				</div>

				{isReplying && currentUser && (
					<div className="mt-2">
						<CommentForm
							comment={`@${comment.user?.username} `}
							postId={comment.post_id}
							currentUserId={currentUser?.id}
							parentCommentId={comment.id}
							onSubmit={() => {
								setIsReplying(false);
							}}
							onReplyAdded={handleReplyAdded}
						/>
					</div>
				)}
			</article>

			{/* Recursively render nested replies */}
			{comment.replies.length > 0 && (
				<div className={cn("", depth > 0 && "border-l border-primary-34/10")}>
					{comment.replies.map((reply: CommentTreeNode) => (
						<CommentPost
							key={reply.id}
							comment={reply}
							currentUser={currentUser}
							depth={depth + 1}
							onReplyAdded={onReplyAdded}
						/>
					))}
				</div>
			)}
		</div>
	);
};
